/* eslint-disable max-lines */

import { LineSeriesOption } from 'echarts';
import { PieSeriesOption } from 'echarts/charts';
import type {
  BarSeriesOption,
  CallbackDataParams,
  DatasetOption,
  EChartsOption,
  GridOption,
  LegendComponentOption,
  LegendOption,
  SeriesOption,
  XAXisOption,
  YAXisOption,
} from 'echarts/types/dist/shared';

import {
  ChartFactory,
  DatasetBase,
  EXPORT_TRUNCATE_LEGEND_LENGTH,
  ReportsChartOptions,
  ReportsEChartsOption,
} from '@core/reports-types';
import { truncateReportsLabel } from '@core/reports-util';

import { init } from './echarts';
import { createChartMediaQueryRules } from './media-query';
import { getChartThemeInitOptions } from './theme';

// every number is in pixels
// and not necessarily accurate. Taken from svg investigation

const legendSizes = {
  rowHeight: 13,
  rowSpacing: 15,
  indicatorSpace: 20,
  letterWidth: 6,
  marginBetweenElements: 10,
};

const axisLabelDefaults = {
  lineHeight: 14,
};

export const defaultGridLayout = {
  topNoLegend: legendSizes.rowSpacing,
  top: legendSizes.rowHeight + legendSizes.rowSpacing * 2,
  right: 20,
  rightWithDataZoom: 60,
  left: 20,
  leftNotYAxisCategory: 50,
  // line height of graphic and some padding
  bottom: 20 + 16,
};

export const reportsEchartsDefaultGridLayout = defaultGridLayout;

const legendDefault = {
  show: true,
  type: 'scroll',
  animationDurationUpdate: 0,
};

export const chartFactory: ChartFactory = (element, theme, devicePixelRatio, locale, isExport) =>
  init(element, getChartThemeInitOptions(theme), {
    devicePixelRatio,
    locale,
    renderer: isExport ? 'svg' : 'canvas',
  });

export const isYAxisCategoryType = (opts: { yAxis?: { type?: string } }) =>
  opts?.yAxis?.type === 'category';

const legend = (chartOpts: ReportsChartOptions | ReportsEChartsOption, showLegend?: boolean) => {
  let show = chartOpts.legend?.show ?? showLegend;
  // Only auto-enable the legend from the column count when the caller has not
  // set `show` explicitly — an explicit `show: false` must be respected.
  if (show === undefined && 'dataset' in chartOpts && chartOpts.dataset) {
    const source = chartOpts.dataset.source;
    show = Array.isArray(source) ? (<any>source[0]).length > 2 : false;
  }
  return {
    ...legendDefault,
    ...(chartOpts.legend || {}),
    show,
  };
};

export const calculateGridBottomOffset = (chartOpts: ReportsEChartsOption): number => {
  if (isYAxisCategoryType(chartOpts)) {
    return defaultGridLayout.bottom;
  }

  let result = defaultGridLayout.bottom;

  // additional space required when x axis is either category or time
  if (chartOpts.xAxis?.type !== 'value') {
    result += 16;
  }

  return result;
};

export const calculateGridOffsetLeft = (chartOpts: any, yAxisLabelWidth = 0) =>
  isYAxisCategoryType(chartOpts)
    ? defaultGridLayout.left + yAxisLabelWidth
    : defaultGridLayout.leftNotYAxisCategory;

export const truncatedFormatterFactory =
  (length: number, formatter: (text: string) => string = s => s) =>
  (text: string) => {
    const label = formatter(text);
    return label.length > length ? truncateReportsLabel(label, length) : label;
  };

const grid = <T extends ReportsEChartsOption>(chartOpts: T) => {
  const leftOffset = calculateGridOffsetLeft(chartOpts);
  const bottomOffset = calculateGridBottomOffset(chartOpts);

  const top = legend(chartOpts).show ? defaultGridLayout.top : defaultGridLayout.topNoLegend;

  return {
    ...defaultGridLayout,
    ...(chartOpts.grid || {}),
    show: false,
    top,
    left: leftOffset,
    bottom: bottomOffset,
    right: chartOpts.dataZoom ? defaultGridLayout.rightWithDataZoom : defaultGridLayout.right,
    containLabel: false,
  };
};

export const lineChartCommonOptions = <
  T extends ReportsEChartsOption & {
    series: LineSeriesOption | LineSeriesOption[];
    dataset: ReportsEChartsOption['dataset'];
  },
>(
  chartOpts: T,
): T & { grid: GridOption; legend: LegendOption; xAxis: XAXisOption; yAxis: YAXisOption } => {
  const series = Array.isArray(chartOpts.series)
    ? chartOpts.series.map(x => ({
        ...x,
        symbol: 'circle',
      }))
    : {
        ...(chartOpts.series ?? {}),
        symbol: 'circle',
      };

  return {
    ...chartOpts,
    series,
    grid: grid(chartOpts),
    legend: legend(chartOpts),
    xAxis: { axisLabel: axisLabelDefaults, ...(chartOpts.xAxis || {}) },
    yAxis: {
      boundaryGap: ['0%', '1%'],
      ...(chartOpts.yAxis || {}),
    },
  };
};

export const barChartMediaOptions = (chartOpts: ReportsEChartsOption) => [
  ...(isYAxisCategoryType(chartOpts)
    ? createChartMediaQueryRules([
        {
          minW: 0,
          maxW: 300,
          opts: {
            yAxis: {
              axisLabel: {
                formatter: truncatedFormatterFactory(
                  15,
                  // @ts-ignore
                  <(s: string) => string>chartOpts.yAxis?.axisLabel?.formatter,
                ),
              },
            },
            grid: {
              containLabel: true,
            },
          },
        },
        {
          minW: 300,
          maxW: 500,
          opts: {
            yAxis: {
              axisLabel: {
                formatter: truncatedFormatterFactory(
                  26,
                  // @ts-ignore
                  <(s: string) => string>chartOpts.yAxis?.axisLabel?.formatter,
                ),
              },
            },
            grid: {
              containLabel: true,
            },
          },
        },
        {
          minW: 500,
          opts: {
            yAxis: {
              axisLabel: {
                formatter: truncatedFormatterFactory(
                  45,
                  // @ts-ignore
                  <(s: string) => string>chartOpts.yAxis?.axisLabel?.formatter,
                ),
              },
            },
            grid: {
              containLabel: true,
            },
          },
        },
      ])
    : []),
];

export const barChartCommonOptions = <T extends ReportsEChartsOption>(
  chartOpts: T,
): T & {
  grid: {
    top: number;
    left: number;
    right: number;
  };
} => {
  const isYAxisTypeCategory = isYAxisCategoryType(chartOpts);

  return {
    ...chartOpts,
    grid: {
      ...grid(chartOpts),
      containLabel: false,
    },
    legend: legend(chartOpts),
    xAxis: {
      axisLabel: axisLabelDefaults,
      ...(chartOpts.xAxis || {}),
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      boundaryGap: isYAxisTypeCategory ? true : ['0%', '1%'],
      ...(chartOpts.yAxis || {}),
    },
    media: [...(chartOpts?.media ?? []), ...barChartMediaOptions(chartOpts)],
  };
};

export const BAR_CHART_BAR_MAX_WIDTH = 44;

export const getChartOptions = (
  chartType: 'column' | 'bar' | 'line' | 'pie',
  data: ReportsChartOptions,
): ReportsChartOptions => {
  switch (chartType) {
    case 'bar':
    case 'column':
      return barChartCommonOptions(data);
    case 'line':
      return lineChartCommonOptions(data);
    case 'pie':
      return pieChartCommonOptions(<ReportsChartOptions & { series: [PieSeriesOption] }>data);
    default:
      throw new Error(`Unknown chart type "${chartType}"`);
  }
};

const pieChartSeriesWithDefaults = (chartOpts: ReportsEChartsOption) => {
  const series = Array.isArray(chartOpts.series) ? chartOpts.series[0] : chartOpts.series;
  return {
    ...series,
    type: 'pie',
    minShowLabelAngle: 2,
    minAngle: 2,
    avoidLabelOverlap: true,
    radius: '35%',
    top: 20,
    bottom: 20,
    right: defaultGridLayout.right,
    left: defaultGridLayout.left,
    label: {
      show: true,
      position: 'outer',
      alignTo: 'labelLine',
      formatter: ['{b}', `{pct|{d}%}`].join('\n'),
      ...((<any>series)?.label || {}),
    },
    labelLine: {
      show: true,
      length: 25,
      length2: 25,
      minTurnAngle: 45,
      maxSurfaceAngle: 45,
    },
  };
};

// eslint-disable-next-line max-lines-per-function
export const pieChartMediaOptions = () =>
  createChartMediaQueryRules([
    {
      minW: 0,
      maxW: 250,
      opts: {
        series: [
          {
            minShowLabelAngle: 8,
            radius: '35%',
            label: {
              bleedMargin: 0,
              distanceToLabelLine: 2,
            },
            labelLine: {
              alignTo: 'labelLine',
              length: 15,
              length2: 10,
              minTurnAngle: 90,
              maxSurfaceAngle: 180,
            },
          },
        ],
      },
    },
    {
      minW: 250,
      maxW: 700,
      opts: {
        series: [
          {
            minShowLabelAngle: 8,
            radius: '55%',
            label: {
              bleedMargin: 0,
              distanceToLabelLine: 2,
            },
            labelLine: {
              alignTo: 'labelLine',
              length: 15,
              length2: 10,
              minTurnAngle: 45,
              maxSurfaceAngle: 180,
            },
          },
        ],
      },
    },
    {
      minW: 700,
      maxW: Number.POSITIVE_INFINITY,
      opts: {
        label: {
          alignTo: 'labelLine',
        },
        series: [
          {
            minShowLabelAngle: 2,
            radius: '65%',
            label: {
              bleedMargin: 4,
              distanceToLabelLine: 4,
            },
            labelLine: {
              alignTo: 'labelLine',
              length: 5,
              length2: 75,
              minTurnAngle: 0,
              maxSurfaceAngle: 90,
            },
          },
        ],
      },
    },
  ]);

export const pieChartCommonOptions = <
  T extends {
    dataset: DatasetOption;
    media?: ReportsChartOptions['media'];
    legend?: ReportsChartOptions['legend'];
    series: [PieSeriesOption];
  },
>(
  chartOpts: T,
): T => ({
  ...chartOpts,
  legend: legend(chartOpts),
  series: [pieChartSeriesWithDefaults(chartOpts)],
  media: [...(<any>chartOpts.media ?? []), ...pieChartMediaOptions()],
  xAxis: {
    show: false,
  },
  yAxis: {
    show: false,
  },
  grid: {
    show: false,
  },
});

const calculateVerticalBarChartLegendHeight = (
  dataset: DatasetBase,
  chartWidth: number,
  truncateLegendLabels?: boolean,
) => {
  const items = dataset.dimensions.slice(1);
  const availableSpace = chartWidth - defaultGridLayout.left - defaultGridLayout.right;

  const rowsCount = items
    .map(
      ({ name }) =>
        (truncateLegendLabels ? EXPORT_TRUNCATE_LEGEND_LENGTH : name.length) *
          legendSizes.letterWidth +
        legendSizes.indicatorSpace +
        legendSizes.marginBetweenElements,
    )
    .reduce(
      (rows, curr) => {
        const currentRowLength = rows[rows.length - 1];
        if (currentRowLength + curr > availableSpace) {
          return [...rows, curr];
        }
        return [...rows.slice(0, -1), rows[rows.length - 1] + curr];
      },
      <number[]>[0],
    ).length;

  return rowsCount * (legendSizes.rowHeight + legendSizes.rowSpacing) + legendSizes.rowSpacing;
};

// eslint-disable-next-line max-lines-per-function
export const exportOptions = (
  chartOpts: EChartsOption,
  dataset: DatasetBase,
  chartWidth: number,
  truncateLegendLabels?: boolean,
): EChartsOption => {
  const updateGrid = (grid: GridOption = {}) => {
    let top = defaultGridLayout.topNoLegend;

    const legendShow = Array.isArray(chartOpts.legend)
      ? chartOpts.legend[0].show
      : chartOpts.legend?.show;

    if (legendShow) {
      top += calculateVerticalBarChartLegendHeight(dataset, chartWidth, truncateLegendLabels);
    }

    return {
      ...grid,
      top,
    };
  };
  const updateLegend = (
    legend: LegendOption = {},
    truncateLabels?: boolean,
  ): LegendComponentOption => {
    if (!truncateLabels) {
      return {
        ...legend,
        type: 'plain',
        textStyle: {
          overflow: 'truncate',
          // width is in fact max width
          width: chartWidth - defaultGridLayout.left - defaultGridLayout.right,
        },
      };
    }
    return {
      ...legend,
      type: 'plain',
      formatter: (x: string) => {
        let name = x;
        // if formatter is a string, there is currently no way to make this work
        // But we do use functions everywhere.
        // TODO: It could actually be mandated by types
        if (typeof legend.formatter === 'function') {
          name = legend.formatter(name);
        }
        return truncateReportsLabel(name);
      },
    };
  };

  const getSeriesLabel = (
    isXAxisValue: boolean,
    seriesLength: number,
    seriesIndex: number,
  ): BarSeriesOption['label'] => ({
    silent: true,
    // Only show for the last entry
    show: seriesIndex === seriesLength - 1,
    position: isXAxisValue ? 'right' : 'top',
    formatter: (x: CallbackDataParams) => {
      const value = (<any[]>x.value).slice(1).reduce((acc, curr) => acc + curr, 0);
      return value && value !== 0 ? value : '';
    },
  });

  const updateSeries = (isXAxisValue: boolean, series: SeriesOption[]) =>
    series.map((y, index) => {
      if (y.type === 'bar' && !y.label?.show) {
        return {
          ...y,
          label: getSeriesLabel(isXAxisValue, series.length, index),
        };
      }
      return y;
    });

  const isXAxisValue = Array.isArray(chartOpts.xAxis)
    ? chartOpts.xAxis[0].type === 'value'
    : chartOpts.xAxis?.type === 'value';

  return {
    ...chartOpts,
    animation: false,
    grid: Array.isArray(chartOpts.grid)
      ? [updateGrid(chartOpts.grid[0])]
      : updateGrid(chartOpts.grid),
    legend: Array.isArray(chartOpts.legend)
      ? [updateLegend(chartOpts.legend[0], truncateLegendLabels)]
      : updateLegend(chartOpts.legend, truncateLegendLabels),
    series: updateSeries(
      isXAxisValue,
      Array.isArray(chartOpts.series) ? chartOpts.series! : [chartOpts.series!],
    ),
    labelLayout: {
      hideOverlap: true,
    },
    dataZoom: [
      {
        filterMode: 'none',
        show: false,
        startValue: 0,
        endValue: Infinity,
      },
    ],
  };
};

const verticalBarRowHeight = (barWidth: number) => 1.5 * barWidth;
const xAxisLabelsHeight = 8 + 14;

export const exportVerticalChartHeight = (
  barWidth: number,
  dataset: DatasetBase,
  chartWidth?: number,
  truncateLabels?: boolean,
) => {
  const barHeight = verticalBarRowHeight(barWidth);
  const rows = dataset.datasetSize.rows - 1;
  const top = !chartWidth
    ? defaultGridLayout.top
    : calculateVerticalBarChartLegendHeight(dataset, chartWidth, truncateLabels);

  return barHeight * rows + top + defaultGridLayout.bottom + xAxisLabelsHeight;
};
